'use client'
import React from 'react'
import styled from 'styled-components'
import { useConfig } from '@/contexts/ConfigContext'
import { useLanguage } from '@/contexts/LanguageContext'
import ModuleContainer from './ModuleContainer'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import CheckIcon from '@mui/icons-material/Check'
import KeySelector from '@/components/CustomInput/KeySelector'
import { useUI } from '@/contexts/UIContext'
import TerminalIcon from '@mui/icons-material/Terminal'
import FlashOnIcon from '@mui/icons-material/FlashOn'
import HandymanIcon from '@mui/icons-material/Handyman'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import HistoryIcon from '@mui/icons-material/History'
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep'
import Tooltip from '@mui/material/Tooltip'
import SportsHandballIcon from '@mui/icons-material/SportsHandball'
import SwipeIcon from '@mui/icons-material/Swipe'
import VolumeOffIcon from '@mui/icons-material/VolumeOff'
import MouseIcon from '@mui/icons-material/Mouse'
import VolumeUpIcon from '@mui/icons-material/VolumeUp'
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { getItemIcon } from '@/constants/itemIcons'

const Binds_Controls = styled.div`
    display: flex;
    flex-direction: column;
    gap: 15px;
    transition: opacity 0.2s ease;
    width: 100%;
`

const Binds_ModuleBody = styled.div`
    display: flex;
    flex-direction: column;
    border-radius: 12px;
    overflow: hidden;
    width: 100%;
`

const Binds_LabelText = styled.span`
    font-size: 11px;
    font-weight: 400;
    color: rgba(255, 255, 255, 0.5);
    text-transform: uppercase;
    letter-spacing: 0.8px;
    display: flex;
    align-items: center;
    gap: 6px;
`

const Binds_ActionButton = styled.div`
    background: ${({ $danger }) => ($danger ? 'rgba(239, 68, 68, 0.1)' : 'rgba(99, 102, 241, 0.1)')};
    border: 1px solid ${({ $danger }) => ($danger ? 'rgba(239, 68, 68, 0.3)' : 'rgba(255, 255, 255, 0.05)')};
    color: ${({ $danger }) => ($danger ? '#ef4444' : '#fff')};
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 0.70rem;
    font-weight: 600;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    white-space: nowrap;
    opacity: ${({ disabled }) => (disabled ? 0.3 : 1)};
    pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};

    &:hover {
        background: ${({ $danger }) => ($danger ? 'rgba(239, 68, 68, 0.2)' : 'rgba(99, 102, 241, 0.15)')};
        color: ${({ $danger }) => ($danger ? '#ff5555' : 'white')};
        border-color: ${({ $danger }) => ($danger ? '#ef4444' : '#6366f1')};
        transform: ${({ disabled }) => (disabled ? 'none' : 'translateY(-1px)')};
    }
`

const SectionRow = styled.div`
    display: flex;
    gap: 20px;
    width: 100%;
    margin: 15px 0;
    align-items: flex-start;

    &:last-child {
        margin-bottom: 0;
    }
`

const SectionIconBinder = styled.div`
    display: none;
    align-items: center;
    justify-content: center;
    width: 50px;
    height: 50px;
    min-width: 50px;
    border-radius: 12px;
    background: ${({ $bg }) => $bg || 'rgba(255,255,255,0.05)'};
    color: ${({ $color }) => $color || '#fff'};
    box-shadow: 0 0 15px ${({ $color }) => $color ? `${$color}22` : 'rgba(0,0,0,0)'};
    border: 1px solid ${({ $color }) => $color ? `${$color}44` : 'transparent'};
    margin-top: 15px;

    svg {
        font-size: 28px;
        filter: drop-shadow(0 0 5px ${({ $color }) => $color || 'rgba(255,255,255,0.5)'});
    }
`

const SectionContent = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 20px;
    width: 100%;
`

const VisualBarContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
    flex: 1;

    &::before {
        display: none;
    }
`


const ItemIcon = styled.img`
    width: 52px;
    height: 32px;
    object-fit: contain;
    filter: brightness(0) invert(1);
    opacity: 0.9;
`;

const GrenadeRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 12px;
`;

const GrenadeIconColumn = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    min-width: 36px;
    opacity: 0.7;
    margin-top: 12px;
`;

const GrenadeLabelColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    align-items: center;
    justify-content: center;
`;
const VisualBar = styled.div`
    display: flex;
    background: ${({ $bg }) => $bg || 'rgba(0, 0, 0, 0.2)'};
    border: 1px solid ${({ $color }) => $color ? `${$color}44` : 'rgba(255, 255, 255, 0.05)'};
    border-radius: 12px;
    overflow: hidden;
    padding: 24px 0;
    transition: all 0.3s ease;
    flex-wrap: wrap;
    align-items: flex-end;
    
    &:hover {
        border-color: ${({ $color }) => $color ? `${$color}88` : 'rgba(255, 255, 255, 0.1)'};
        background: ${({ $bg, $color }) => $bg ? `${$bg.replace('0.2', '0.25')}` : ($color ? `${$color}11` : 'rgba(255, 255, 255, 0.03)')};
    }
`

const BarSlot = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    gap: 10px;
    border-right: 1px solid rgba(255, 255, 255, 0.05);
    transition: all 0.2s;
    padding: 0px 15px;

    &:last-child {
        border-right: none;
    }
`

const CustomHeader = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    width: 100%;
    margin-bottom: 4px;
    padding-left: 2px;
    flex-direction: column;
    gap: 10px;
`

const HeaderLabel = styled.span`
    font-size: 0.7rem;
    font-weight: 800;
    color: ${({ $color }) => $color || '#6366f1'};
    opacity: 0.9;
    letter-spacing: 2px;
    text-transform: uppercase;
`

const AutoBuyToggle = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    background: rgba(249, 115, 22, 0.1);
    border: 1px solid rgba(249, 115, 22, 0.2);
    padding: 4px 10px;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        background: rgba(249, 115, 22, 0.2);
        border-color: rgba(249, 115, 22, 0.4);
    }

    span {
        font-size: 0.65rem;
        font-weight: 700;
        color: #f97316;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }
`

const SlotLabel = styled.span`
    font-size: 0.65rem;
    color: rgba(255, 255, 255, 0.75);
    font-weight: 700;
    text-transform: uppercase;
    text-align: center;
`

const Binds_HelpIconWrapper = styled.div`
    display: flex;
    align-items: center;
    color: rgba(99, 102, 241, 0.4);
    cursor: help;
    position: relative;
    transition: color 0.2s;

    &:hover {
        color: #6366f1;
        
        .tooltip {
            opacity: 1;
            visibility: visible;
            transform: translateY(-50%) translateX(0);
        }
    }

    svg {
        font-size: 14px !important;
    }
`

const Binds_Tooltip = styled.div`
    position: absolute;
    top: 50%;
    left: calc(100% + 10px);
    transform: translateY(-50%) translateX(-8px);
    background: #0d1117;
    color: #fff;
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 10px;
    font-weight: 500;
    text-transform: none;
    letter-spacing: 0.2px;
    width: 180px;
    opacity: 0;
    visibility: hidden;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    pointer-events: none;
    z-index: 100;
    border: 1px solid rgba(255, 255, 255, 0.1);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
    text-align: left;
    line-height: 1.4;

    &::after {
        content: '';
        position: absolute;
        top: 50%;
        right: 100%;
        transform: translateY(-50%);
        border-width: 5px;
        border-style: solid;
        border-color: transparent rgba(255, 255, 255, 0.1) transparent transparent;
    }
`

const Checkbox = styled.div`
    width: 20px;
    height: 20px;
    border: 2px solid #444;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    background: ${({ $checked }) => ($checked ? '#6366f1' : 'transparent')};
    border-color: ${({ $checked }) => ($checked ? '#6366f1' : '#444')};
    transition: all 0.2s;
    opacity: ${({ disabled }) => (disabled ? 0.3 : 1)};
    pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};

    svg {
        font-size: 14px;
        color: white;
        opacity: ${({ $checked }) => ($checked ? 1 : 0)};
    }
`

const SelectWrapper = styled.div`
    position: relative;
    width: 170px;
`

const StyledSelect = styled.select`
    width: 100%;
    background: rgba(15, 23, 42, 0.6);
    border: 1px solid rgba(255, 255, 255, 0.1);
    color: white;
    padding: 6px 10px;
    padding-right: 28px;
    border-radius: 6px;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    font-size: 0.75rem;
    appearance: none;
    outline: none;
    transition: all 0.2s;
    font-family: inherit;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
    pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};

    &:focus {
        border-color: #6366f1;
        box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.2);
    }
    
    &:hover {
        border-color: rgba(255, 255, 255, 0.3);
        background: rgba(15, 23, 42, 0.8);
    }

    option {
        background: #0f172a;
        color: white;
    }
`

const ArrowIcon = styled(KeyboardArrowDownIcon)`
    position: absolute;
    right: 6px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    color: rgba(255, 255, 255, 0.5);
    font-size: 1rem !important;
`

export default function BindsSpecialModule({ id, initialExpanded }) {
    const { configData, updateConfig, activeModules, toggleModule, resetSpecialBinds, clearSpecialBinds, configIsLocked } = useConfig()
    const { t } = useLanguage()
    const { showConfirm } = useUI()

    const binds = configData.binds || {}
    const isActive = activeModules.includes('binds_special')

    const handleKeyChange = (key, value) => {
        updateConfig('binds', key, value)
    }

    const handleRestoreDefaults = async (e) => {
        e.stopPropagation()
        if (configIsLocked) return
        const confirmed = await showConfirm({
            title: t('RESTORING_DEFAULTS'),
            message: t('RESTORING_DEFAULTS_CONFIRM'),
            confirmText: t('RESTORE'),
            variant: 'primary',
        })
        if (confirmed) resetSpecialBinds()
    }

    const handleClearAll = async (e) => {
        e.stopPropagation()
        if (configIsLocked) return
        const confirmed = await showConfirm({
            title: t('CLEAR_ALL_BINDS'),
            message: t('CLEAR_ALL_BINDS_CONFIRM'),
            confirmText: t('CLEAR_ALL'),
            variant: 'danger',
        })
        if (confirmed) clearSpecialBinds()
    }

    return (
        <ModuleContainer
            id={id}
            title={t('BINDS_SPECIAL_TITLE')}
            description={t('BINDS_SPECIAL_DESC')}
            icon={AutoFixHighIcon}
            moduleKey="binds_special"
            isActive={isActive}
            onToggle={() => toggleModule('binds_special')}
            minHeight="250px"
            initialExpanded={initialExpanded}
        >
            <Binds_Controls isActive={isActive}>
                {isActive && (
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginBottom: '4px' }}>
                        <Binds_ActionButton onClick={handleRestoreDefaults} disabled={configIsLocked} title={t('RESTORE_DEFAULTS')}>
                            <HistoryIcon style={{ fontSize: 14, marginRight: 4 }} />
                            {t('RESTORE')}
                        </Binds_ActionButton>
                        <Binds_ActionButton $danger onClick={handleClearAll} disabled={configIsLocked} title={t('CLEAR_ALL')}>
                            <DeleteSweepIcon style={{ fontSize: 14, marginRight: 4 }} />
                            {t('CLEAR')}
                        </Binds_ActionButton>
                    </div>
                )}
                <Binds_ModuleBody>

                        {/* GRENADES */}
                        <SectionRow>
                            <SectionIconBinder $color="#f97316" $bg="rgba(249, 115, 22, 0.1)">
                                <FlashOnIcon />
                            </SectionIconBinder>
                            <SectionContent>
                                <VisualBarContainer $color="#f97316">
                                    <CustomHeader>
                                        <HeaderLabel $color="#f97316">{t('GRENADES_BINDS')}</HeaderLabel>
                                        <Tooltip title={t('AUTO_BUY_GRENADES_DESC')} arrow placement="top">
                                            <AutoBuyToggle
                                                onClick={() => !configIsLocked && handleKeyChange('grenade_binds_buy', !binds.grenade_binds_buy)}
                                                style={{ opacity: configIsLocked ? 0.5 : 1, pointerEvents: configIsLocked ? 'none' : 'auto' }}
                                            >
                                                <ShoppingCartIcon style={{ fontSize: 14, color: '#f97316' }} />
                                                <span>{t('AUTO_BUY')}</span>
                                                <Checkbox $checked={binds.grenade_binds_buy} style={{ width: 14, height: 14, marginLeft: 4, borderColor: '#f97316', background: binds.grenade_binds_buy ? '#f97316' : 'transparent' }}>
                                                    <CheckIcon style={{ fontSize: 10 }} />
                                                </Checkbox>
                                            </AutoBuyToggle>
                                        </Tooltip>
                                    </CustomHeader>

                                    <VisualBar $color="#f97316" $bg="rgba(249, 115, 22, 0.05)">
                                        <BarSlot>
                                            <GrenadeRow>
                                                <GrenadeIconColumn>
                                                    <ItemIcon src={getItemIcon('flashbang')} alt="" />
                                                </GrenadeIconColumn>
                                                <GrenadeLabelColumn>
                                                    <SlotLabel>{t('FLASHBANG')}</SlotLabel>
                                                    <KeySelector value={binds.grenade_flash} onChange={(v) => handleKeyChange('grenade_flash', v)} fieldPath="binds.grenade_flash" disabled={!isActive || configIsLocked} />
                                                </GrenadeLabelColumn>
                                            </GrenadeRow>
                                        </BarSlot>
                                        <BarSlot>
                                            <GrenadeRow>
                                                <GrenadeIconColumn>
                                                    <ItemIcon src={getItemIcon('smokegrenade')} alt="" />
                                                </GrenadeIconColumn>
                                                <GrenadeLabelColumn>
                                                    <SlotLabel>{t('SMOKE')}</SlotLabel>
                                                    <KeySelector value={binds.grenade_smoke} onChange={(v) => handleKeyChange('grenade_smoke', v)} fieldPath="binds.grenade_smoke" disabled={!isActive || configIsLocked} />
                                                </GrenadeLabelColumn>
                                            </GrenadeRow>
                                        </BarSlot>
                                        <BarSlot>
                                            <GrenadeRow>
                                                <GrenadeIconColumn>
                                                    <ItemIcon src={getItemIcon('molotov')} alt="" />
                                                </GrenadeIconColumn>
                                                <GrenadeLabelColumn>
                                                    <SlotLabel>{t('MOLOTOV')}</SlotLabel>
                                                    <KeySelector value={binds.grenade_molotov} onChange={(v) => handleKeyChange('grenade_molotov', v)} fieldPath="binds.grenade_molotov" disabled={!isActive || configIsLocked} />
                                                </GrenadeLabelColumn>
                                            </GrenadeRow>
                                        </BarSlot>
                                        <BarSlot>
                                            <GrenadeRow>
                                                <GrenadeIconColumn>
                                                    <ItemIcon src={getItemIcon('hegrenade')} alt="" />
                                                </GrenadeIconColumn>
                                                <GrenadeLabelColumn>
                                                    <SlotLabel>{t('HE_GRENADE')}</SlotLabel>
                                                    <KeySelector value={binds.grenade_he} onChange={(v) => handleKeyChange('grenade_he', v)} fieldPath="binds.grenade_he" disabled={!isActive || configIsLocked} />
                                                </GrenadeLabelColumn>
                                            </GrenadeRow>
                                        </BarSlot>
                                        <BarSlot>
                                            <GrenadeRow>
                                                <GrenadeIconColumn>
                                                    <ItemIcon src={getItemIcon('decoy')} alt="" />
                                                </GrenadeIconColumn>
                                                <GrenadeLabelColumn>
                                                    <SlotLabel>{t('DECOY')}</SlotLabel>
                                                    <KeySelector value={binds.grenade_decoy} onChange={(v) => handleKeyChange('grenade_decoy', v)} fieldPath="binds.grenade_decoy" disabled={!isActive || configIsLocked} />
                                                </GrenadeLabelColumn>
                                            </GrenadeRow>
                                        </BarSlot>
                                    </VisualBar>
                                </VisualBarContainer>
                            </SectionContent>
                        </SectionRow>


                        {/* ADVANCED TOOLS */}
                        <SectionRow>
                            <SectionIconBinder $color="#06b6d4" $bg="rgba(6, 182, 212, 0.1)">
                                <TerminalIcon />
                            </SectionIconBinder>
                            <SectionContent>
                                <VisualBarContainer $label={t('ADVANCED_TOOLS_LABEL')} $color="#06b6d4">
                                    <VisualBar $color="#06b6d4" $bg="rgba(6, 182, 212, 0.05)">
                                        <BarSlot>
                                            <GrenadeRow>
                                                <GrenadeIconColumn>
                                                    <SportsHandballIcon style={{ fontSize: 32 }} />
                                                </GrenadeIconColumn>
                                                <GrenadeLabelColumn>
                                                    <SlotLabel>{t('JUMP_THROW')}</SlotLabel>
                                                    <KeySelector value={binds.jump_throw_key} onChange={(v) => handleKeyChange('jump_throw_key', v)} fieldPath="binds.jump_throw_key" disabled={!isActive || configIsLocked} />
                                                </GrenadeLabelColumn>
                                            </GrenadeRow>
                                        </BarSlot>
                                        <BarSlot>
                                            <GrenadeRow>
                                                <GrenadeIconColumn>
                                                    <SwipeIcon style={{ fontSize: 32 }} />
                                                </GrenadeIconColumn>
                                                <GrenadeLabelColumn>
                                                    <SlotLabel>{t('SWITCH_HANDS_BIND')}</SlotLabel>
                                                    <KeySelector value={binds.viewmodel_switchhands_bind} onChange={(v) => handleKeyChange('viewmodel_switchhands_bind', v)} fieldPath="binds.viewmodel_switchhands_bind" disabled={!isActive || configIsLocked} />
                                                </GrenadeLabelColumn>
                                            </GrenadeRow>
                                        </BarSlot>
                                    </VisualBar>
                                </VisualBarContainer>
                            </SectionContent>
                        </SectionRow>



                        {/* EQUIPMENT & AUDIO */}
                        <SectionRow>
                            <SectionIconBinder $color="#6366f1" $bg="rgba(99, 102, 241, 0.1)">
                                <HandymanIcon />
                            </SectionIconBinder>
                            <SectionContent>
                                <VisualBarContainer $label={t('EQUIPMENT_AUDIO')} $color="#6366f1">
                                    <VisualBar $color="#6366f1" $bg="rgba(99, 102, 241, 0.05)" style={{ width: '100%' }}>
                                        <BarSlot>
                                            <GrenadeRow>
                                                <GrenadeIconColumn>
                                                    <MouseIcon style={{ fontSize: 32 }} />
                                                </GrenadeIconColumn>
                                                <GrenadeLabelColumn>
                                                    <SlotLabel>{t('MWHEEL_JUMP')}</SlotLabel>
                                                    <SelectWrapper>
                                                        <StyledSelect value={binds.mwheel_jump} onChange={(e) => handleKeyChange('mwheel_jump', e.target.value)} disabled={!isActive || configIsLocked}>
                                                            <option value="none">{t('DISABLED')}</option>
                                                            <option value="up">{t('WHEEL_UP')}</option>
                                                            <option value="down">{t('WHEEL_DOWN')}</option>
                                                            <option value="both">{t('BOTH')}</option>
                                                        </StyledSelect>
                                                        <ArrowIcon />
                                                    </SelectWrapper>
                                                </GrenadeLabelColumn>
                                            </GrenadeRow>
                                        </BarSlot>
                                        <BarSlot>
                                            <GrenadeRow>
                                                <GrenadeIconColumn>
                                                    <VolumeUpIcon style={{ fontSize: 32 }} />
                                                </GrenadeIconColumn>
                                                <GrenadeLabelColumn>
                                                    <Tooltip title={t('CYCLE_VOLUME_DESC')} arrow placement="top">
                                                        <SlotLabel style={{ cursor: 'help', textDecoration: 'underline dotted', textUnderlineOffset: '2px' }}>{t('CYCLE_VOLUME')}</SlotLabel>
                                                    </Tooltip>
                                                    <KeySelector value={binds.bind_vol_cycle} onChange={(v) => handleKeyChange('bind_vol_cycle', v)} fieldPath="binds.bind_vol_cycle" disabled={!isActive || configIsLocked} />
                                                </GrenadeLabelColumn>
                                            </GrenadeRow>
                                        </BarSlot>
                                        <BarSlot>
                                            <GrenadeRow>
                                                <GrenadeIconColumn>
                                                    <RecordVoiceOverIcon style={{ fontSize: 32 }} />
                                                </GrenadeIconColumn>
                                                <GrenadeLabelColumn>
                                                    <Tooltip title={t('CYCLE_VOICE_DESC')} arrow placement="top">
                                                        <SlotLabel style={{ cursor: 'help', textDecoration: 'underline dotted', textUnderlineOffset: '2px' }}>{t('CYCLE_VOICE')}</SlotLabel>
                                                    </Tooltip>
                                                    <KeySelector value={binds.bind_voice_cycle} onChange={(v) => handleKeyChange('bind_voice_cycle', v)} fieldPath="binds.bind_voice_cycle" disabled={!isActive || configIsLocked} />
                                                </GrenadeLabelColumn>
                                            </GrenadeRow>
                                        </BarSlot>
                                        <BarSlot>
                                            <GrenadeRow>
                                                <GrenadeIconColumn>
                                                    <VolumeOffIcon style={{ fontSize: 30 }} />
                                                </GrenadeIconColumn>
                                                <GrenadeLabelColumn>
                                                    <SlotLabel>{t('MUTE_ALL')}</SlotLabel>
                                                    <KeySelector value={binds.bind_mute_all} onChange={(v) => handleKeyChange('bind_mute_all', v)} fieldPath="binds.bind_mute_all" disabled={!isActive || configIsLocked} />
                                                </GrenadeLabelColumn>
                                            </GrenadeRow>
                                        </BarSlot>
                                    </VisualBar>
                                </VisualBarContainer>
                            </SectionContent>
                        </SectionRow>

                </Binds_ModuleBody>
            </Binds_Controls>
        </ModuleContainer>
    )
}
